/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import Modal from "react-modal";
import Icon from "uis/Icon";
import {
  CancelContainer,
  Container,
  TitleSection,
  TagSection,
  Content,
  EmptyImage,
  GoogleInfoContainer,
  ImageContainer,
  InfoSection,
  Label,
  PrivateInfoContainer,
  ResourceFrame,
  Image,
  TitleContainer,
  InfoItemContainer,
  ButtonContainer,
} from "./RoomDetailInfoModal.styles";
import UI from "@mile-meeting/mile-ui";
import { TMeetingRoom } from "apis/meetings/meeting-room";
import React from "react";

const modalStyle = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    width: "480px",
    borderRadius: "16px",
    transform: "translate(-50%, -50%)",
    padding: "0",
  },
  overlay: {
    backgroundColor: "var(--component-color-material-dimmer)",
    zIndex: 1000,
  },
};

Modal.setAppElement("#root");

type RoomInfoModalProps = {
  modalIsOpen: boolean;
  closeModal: () => void;
  room: TMeetingRoom;
  onEdit?: () => void;
  onDelete?: () => void;
  hasSuperAdminRoles?: boolean;
};

const RoomInfoModal: React.FC<RoomInfoModalProps> = (props) => {
  const { modalIsOpen, closeModal, room, onEdit, onDelete, hasSuperAdminRoles = false } = props;
  const {
    name,
    capacity,
    description,
    isInsideTabletUsed,
    isOutsideTabletUsed,
    building,
    floor,
    image,
    meetingRoomIntegration,
  } = room;

    //* INFO: 태블릿 사용 여부에 따라 태그 텍스트를 반환하는 함수
  const getTagText = () => {
    if( isInsideTabletUsed && isOutsideTabletUsed) {
      return "내/외부 태블릿 사용";
    } else if (isInsideTabletUsed) {
      return "내부 태블릿 사용";
    } else if (isOutsideTabletUsed) {
      return "외부 태블릿 사용";
    }
    return "태블릿 미사용";
  }

  return (
    <Modal isOpen={modalIsOpen} onRequestClose={closeModal} style={modalStyle}>
      <Container>
        <CancelContainer>
          <UI.IconButton.Normal
            type="primary"
            icon="x"
            size={20}
            color="var(--alias-color-label-normal)"
            onClick={closeModal}
          />
        </CancelContainer>
        <TitleContainer>
          <TitleSection>
            <TagSection>
              <UI.Tag
                color={isInsideTabletUsed || isOutsideTabletUsed ? "blue" : "gray"}
                size="small"
                label={getTagText()}
              />
            </TagSection>
            <UI.Typography type="title03" fontStyle="bold" color="var(--alias-color-label-noraml)">
              {name}
            </UI.Typography>
          </TitleSection>
          {meetingRoomIntegration && (
            <GoogleInfoContainer>
              <ResourceFrame>
                <UI.Icon name="google" size={20} />
                <UI.Typography color="var(--alias-color-label-normal)" type="body01 normal" fontStyle="medium">
                  {meetingRoomIntegration.resourceName}
                </UI.Typography>
              </ResourceFrame>
              <PrivateInfoContainer>
                <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                  비공개 회의 데이터
                </UI.Typography>
                {meetingRoomIntegration.privateMeetingDataCollected ? (
                  <UI.Tag color="cyan" label="수집" size="small" />
                ) : (
                  <UI.Tag color="gray" label="미수집" size="small" />
                )}
              </PrivateInfoContainer>
            </GoogleInfoContainer>
          )}
        </TitleContainer>
        <Content>
          <InfoSection>
            <ImageContainer>
              <Label>
                <UI.Icon name="image" size={16} color="var(--alias-color-label-assistive)" />
                <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                  회의실 이미지
                </UI.Typography>
              </Label>
              {image && image !== "" ? (
                <Image src={image} />
              ) : (
                <EmptyImage>
                  <UI.Icon name="image" size={40} color="var(--alias-color-label-neutral)" />
                </EmptyImage>
              )}
            </ImageContainer>
            <InfoItemContainer>
              <Label>
                <UI.Icon name="office" color="var(--alias-color-label-assistive)" size={16} />
                <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                  위치
                </UI.Typography>
              </Label>
              <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
                {building?.name && floor?.name && `${building?.name} ${floor?.name}`}
                {!building?.name && floor?.name && `건물 미설정 / ${floor?.name}`}
                {building?.name && !floor?.name && `${building?.name} / 층 미설정`}
                {!building?.name && !floor?.name && `건물/층 미설정`}
              </UI.Typography>
            </InfoItemContainer>
            <InfoItemContainer>
              <Label>
                <UI.Icon name="people" color="var(--alias-color-label-assistive)" size={16} />
                <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                  수용 인원
                </UI.Typography>
              </Label>
              <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
                {capacity}인
              </UI.Typography>
            </InfoItemContainer>
            <InfoItemContainer>
              <Label>
                <UI.Icon name="dotHorizontalCircle" color="var(--alias-color-label-assistive)" size={16} />
                <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                  상세 설명
                </UI.Typography>
              </Label>
              <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
                {description}
              </UI.Typography>
            </InfoItemContainer>
          </InfoSection>
          {hasSuperAdminRoles && (
            <ButtonContainer>
              <UI.Button
                onClick={onEdit}
                title="수정하기"
                size="medium"
                primary={false}
                prefixIcon="modify"
                showPrefixIcon
              />
              <UI.Button
                onClick={onDelete}
                title="삭제하기"
                size="medium"
                primary={false}
                prefixIcon="trashCan"
                showPrefixIcon
                type="outlined"
              />
            </ButtonContainer>
          )}
        </Content>
      </Container>
    </Modal>
  );
};

export default React.memo(RoomInfoModal);
